import type { ChangeEvent } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { textFieldButtonSize } from "../../utils/common";
import { IconButton } from "../Button/IconButton";
import { Input } from "./";
import type { IInputSize, IInputTimeProps } from "./types";

const convert24to12 = (
  time24: string,
): { time12: string; period: "AM" | "PM" } => {
  if (!time24 || time24.length < 2) return { time12: time24, period: "AM" };

  const [hourStr, minutes] = time24.split(":");
  const hour24 = parseInt(hourStr, 10);

  if (isNaN(hour24)) return { time12: time24, period: "AM" };

  let hour12: number;
  let period: "AM" | "PM";

  if (hour24 === 0) {
    hour12 = 12;
    period = "AM";
  } else if (hour24 < 12) {
    hour12 = hour24;
    period = "AM";
  } else if (hour24 === 12) {
    hour12 = 12;
    period = "PM";
  } else {
    hour12 = hour24 - 12;
    period = "PM";
  }

  const hour12Str = hour12.toString().padStart(2, "0");
  return { time12: minutes ? `${hour12Str}:${minutes}` : hour12Str, period };
};

const convert12to24 = (time12: string, period: "AM" | "PM"): string => {
  if (!time12 || time12.length < 2) return time12;

  const [hourStr, minutes] = time12.split(":");
  const hour12 = parseInt(hourStr, 10);

  if (isNaN(hour12)) return time12;

  let hour24: number;

  if (period === "AM") {
    hour24 = hour12 === 12 ? 0 : hour12;
  } else {
    hour24 = hour12 === 12 ? 12 : hour12 + 12;
  }

  const hour24Str = hour24.toString().padStart(2, "0");
  return minutes ? `${hour24Str}:${minutes}` : hour24Str;
};

export const InputTime = (props: IInputTimeProps) => {
  const { onChange, min, max, onPreventInput, disabled, step } = props;
  const isMobile = useMediaQuery("(max-width: 639px)");
  const size: IInputSize = props.size || (isMobile ? "lg" : "md");

  // Parse initial 24h value to 12h + period
  const initialParsed = useMemo(
    () => convert24to12(props.value || ""),
    [props.value],
  );
  const [period, setPeriod] = useState<"AM" | "PM">(initialParsed.period);
  const [displayValue, setDisplayValue] = useState(initialParsed.time12);

  const togglePeriod = useCallback(() => {
    setPeriod((prev) => {
      const newPeriod = prev === "AM" ? "PM" : "AM";
      const value24 = convert12to24(displayValue, newPeriod);
      onChange(value24);
      return newPeriod;
    });
  }, [displayValue, onChange]);

  const previousValue = useRef<string>(displayValue || "");
  const minArray = useMemo(
    () => (min?.split("") ?? []).map((item) => Number(item)),
    [min],
  );
  const maxArray = useMemo(
    () => (max?.split("") ?? []).map((item) => Number(item)),
    [max],
  );

  const checkDisallowTime = useCallback(
    (value: string[]) => {
      const array = value.map((item) => Number(item));
      const hasMin = minArray.length === 5;
      const hasMax = maxArray.length === 5;

      if (hasMin) {
        const hourLessThanMin =
          array[0] < minArray[0] ||
          (array[0] === minArray[0] && array[1] < minArray[1]);
        const isSameHour = array[0] === minArray[0] && array[1] === minArray[1];
        const minuteLessThanMin =
          isSameHour &&
          (array[3] < minArray[3] ||
            (array[3] === minArray[3] && array[4] < minArray[4]));

        if (hourLessThanMin || minuteLessThanMin) {
          return true;
        }
      }

      if (hasMax) {
        const hourGreaterThanMax =
          array[0] > maxArray[0] ||
          (array[0] === maxArray[0] && array[1] > maxArray[1]);
        const isSameHour = array[0] === maxArray[0] && array[1] === maxArray[1];
        const minuteGreaterThanMax =
          isSameHour &&
          (array[3] > maxArray[3] ||
            (array[3] === maxArray[3] && array[4] > maxArray[4]));

        if (hourGreaterThanMax || minuteGreaterThanMax) {
          return true;
        }
      }

      if (typeof step === "number") {
        const firstMinDigit = (isNaN(array[3]) ? 0 : array[3]) * 10;
        const secondMinDigit = isNaN(array[4]) ? 0 : array[4];
        const minute = firstMinDigit + secondMinDigit;
        const divider = firstMinDigit / step;
        const minStep = Math.floor((Math.floor(divider) * step) / 10) * 10;
        const maxStep = Math.floor((Math.ceil(divider) * step) / 10) * 10;
        return !(
          minute % step === 0 ||
          (isNaN(array[4]) &&
            (minStep === firstMinDigit || maxStep === firstMinDigit))
        );
      }

      return false;
    },
    [minArray, maxArray, step],
  );

  const handleChange = useCallback(
    (value: string, e?: ChangeEvent<HTMLInputElement>) => {
      const input = e?.target as HTMLInputElement;
      const currentCursorPosition = input?.selectionStart ?? value.length;
      const cursorPosition = currentCursorPosition - 1;
      let nextCursorPosition = currentCursorPosition;

      // Remove all non-digits first, then handle formatting
      const digitsOnly = value.replace(/[^0-9]/g, "");
      let array = digitsOnly.split("");

      // Auto-format: insert colon after 2nd digit if we have 3+ digits
      if (array.length >= 3) {
        array.splice(2, 0, ":");
      }

      if (previousValue.current.length < value.length || value.length === 1) {
        if (cursorPosition === 0) {
          if (array.length === 1) {
            // 12-hour format: digits 2-9 auto-prefix with "0"
            if (+array[0] > 1) {
              array[1] = array[0];
              array[0] = "0";
              array[2] = ":";
              nextCursorPosition = 3;
            }
          } else {
            // 12-hour format: reject first digit > 1 when editing
            if (+array[0] > 1) {
              array.shift();
              nextCursorPosition = 0;
            } else {
              if (array[3] === ":") {
                array.splice(1, 1);
              }
              // 12-hour format: when first digit is 1, second can only be 0, 1, 2
              if (+array[0] === 1) {
                if (+array[1] > 2) {
                  array[1] = "2";
                }
              }
              // 12-hour format: reject "00" hours
              if (+array[0] === 0 && array[1] === "0") {
                array.splice(1, 1);
              }
            }
          }
        }

        if (cursorPosition === 1) {
          // 12-hour format: "1X" where X > 2 is invalid, "0X" where X = 0 is invalid
          if (array[0] === "1" && +array[1] > 2) {
            array.splice(1, 1);
            nextCursorPosition = 1;
          } else if (array[0] === "0" && array[1] === "0") {
            array.splice(1, 1);
            nextCursorPosition = 1;
          } else {
            if (array.length === 2) {
              array[2] = ":";
              nextCursorPosition = 3;
            } else {
              array.splice(2, 1);
              if (!array.includes(":")) {
                array.splice(2, 0, ":");
              }
            }
          }
        }

        if (cursorPosition === 2) {
          const value = array[2];
          array.splice(2, 1);
          if (+value <= 5) {
            array[2] = ":";
            array[3] = value;
            nextCursorPosition = 4;
          } else {
            nextCursorPosition = 3;
          }
        }

        if (cursorPosition === 3) {
          if (+array[3] > 5) {
            array.splice(3, 1);
            nextCursorPosition = 3;
          }
        }
      } else {
        nextCursorPosition = currentCursorPosition;
      }

      if (array.length > 5) {
        array = array.filter((_, i: number) => i < 5);
      }

      const isDisallowTime = checkDisallowTime(array);
      if (isDisallowTime) {
        onPreventInput?.();
        return;
      }

      const nextValue12 = array.join("");
      previousValue.current = nextValue12;
      setDisplayValue(nextValue12);

      // Convert to 24h before calling parent onChange
      const nextValue24 = convert12to24(nextValue12, period);
      onChange(nextValue24);

      if (cursorPosition !== undefined) {
        if (nextCursorPosition > nextValue12.length) {
          nextCursorPosition = nextValue12.length;
        }

        setTimeout(() => {
          input.setSelectionRange(nextCursorPosition, nextCursorPosition);
        }, 0);
      }
    },
    [onChange, checkDisallowTime, onPreventInput, period],
  );

  const nativeProps = useMemo(() => {
    const nativeProps = { ...props };
    delete nativeProps.onPreventInput;
    return nativeProps;
  }, [props]);

  return (
    <Input
      {...nativeProps}
      value={displayValue}
      autoComplete={nativeProps.autoComplete || "off"}
      ref={props.ref}
      onChange={handleChange}
      endAccessory={
        <IconButton
          tabIndex={-1}
          size={textFieldButtonSize[size]}
          variant="ghost"
          color="neutral"
          disabled={disabled}
          onClick={togglePeriod}
          content={period}
        />
      }
    />
  );
};
